import { useRouter } from "@/_router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/controller";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Container from "@/components/Container";
import PageHeader from "@/components/PageHeader";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import BottomActionBar from "@/components/BottomActionBar";
import CircularProgress from "@/components/CircularProgress";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
} from "@/components/Icons";
import { recordFeatureUsed } from "@/models/feature-adoption";
import { useStillWorking } from "@/models/useStillWorking";
import { promptAiInstructions } from "@/models/useAiInstructions";
import { actions } from "@/controller";

export default function WebFlashcard() {
  const { t } = useTranslation();
  const router = useRouter();
  const transcriptId = router.query?.id as string;
  const transcripts = useAppStore((state) => state.transcripts);
  const transcript = useMemo(
    () => transcripts.find((tr) => tr.id === transcriptId),
    [transcripts, transcriptId]
  );

  const [isLoading, setIsLoading] = useState(
    !transcript?.flashcard || transcript.flashcard.length === 0
  );


  const [progress, setProgress] = useState(0);
  const stillWorking = useStillWorking(isLoading);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const generationHandleRef = useRef<ReturnType<
    typeof actions.aiContent.generate
  > | null>(null);
  const generationIdRef = useRef(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const _startProgress = () => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    setProgress(0);
    intervalIdRef.current = setInterval(() => {
      setProgress((prev) => Math.min(99, prev + 3));
    }, 1000);
  };

  useEffect(() => {
    if (transcriptId && !transcript) {
      void actions.transcript.get(transcriptId);
    }
  }, [transcriptId, transcript]);

  useEffect(() => {
    setIsLoading(true);
    setProgress(0);
    setIsScrolled(false);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  }, [transcriptId]);

  const generateFlashcards = useCallback(async (instructions?: string) => {
    if (!transcriptId) return;
    generationIdRef.current += 1;
    const generationId = generationIdRef.current;
    generationHandleRef.current?.cancel();
    setIsLoading(true);
    _startProgress();
    const handle = actions.aiContent.generate({
      feature: "flashcard",
      transcriptId,
      instructions,
    });
    generationHandleRef.current = handle;
    const completed = await handle.done;
    if (generationId !== generationIdRef.current) return;
    generationHandleRef.current = null;
    // Completion is driven by the poll handle — a store watcher would fire
    // instantly on regeneration while the OLD cards are still cached.
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    setIsLoading(false);
    if (!("error" in completed)) {
      setProgress(100);
      recordFeatureUsed("flashcard");
    }
  }, [transcriptId]);

  useEffect(() => {
    if (!transcript || transcript.flashcard?.length) return;
    // A custom regeneration clears the stored cards; the in-flight guard
    // keeps this auto-start from cancelling that run.
    if (generationHandleRef.current?.key === `${transcriptId}:flashcard`) {
      return;
    }
    void generateFlashcards();
  }, [
    Boolean(transcript),
    Boolean(transcript?.flashcard?.length),
    generateFlashcards,
    transcriptId,
  ]);

  // Unmount-only: abandon whatever generation/poll is still in flight.
  useEffect(
    () => () => {
      generationIdRef.current += 1;
      generationHandleRef.current?.cancel();
      generationHandleRef.current = null;
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    },
    []
  );

  // Content already present with no generation in flight (page load or
  // transcript switch): drop the spinner. Generation completion itself is
  // handle-driven — the old cards are still cached during a regeneration.
  useEffect(() => {
    if (transcript?.flashcard?.length && isLoading && !generationHandleRef.current) {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      setIsLoading(false);
      setProgress(100);
    }
  }, [transcript?.flashcard, isLoading]);

  const flashcards = transcript?.flashcard || [];
  const currentCard = flashcards[currentCardIndex];

  useEffect(() => {
    if (flashcards.length > 0 && currentCardIndex >= flashcards.length) {
      setCurrentCardIndex(0);
      setIsFlipped(false);
    }
  }, [flashcards.length, currentCardIndex]);

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
      setIsFlipped(false);
    }
  };

  const handleNext = () => {
    if (currentCardIndex < flashcards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
      setIsFlipped(false);
    }
  };

  const handleRegenerate = () => {
    if (!transcriptId) return;
    // No cards yet (failed/empty) → plain retry; otherwise regenerating an
    // existing set always collects custom instructions first.
    if (!transcript?.flashcard?.length) {
      setCurrentCardIndex(0);
      setIsFlipped(false);
      void generateFlashcards();
      return;
    }
    promptAiInstructions("flashcard", (instructions) => {
      setCurrentCardIndex(0);
      setIsFlipped(false);
      void generateFlashcards(instructions);
    });
  };

  return (
    <Container reading className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden">
      <BackgroundAmbience />

      <PageHeader isScrolled={isScrolled} onBack={() => router.back()}>
        <button
          onClick={handleRegenerate}
          data-action-id="flashcard.regenerate"
          disabled={isLoading}
          className="icon-btn"
        >
          <SparklesIcon size={18} />
        </button>
      </PageHeader>

      <main
        className="flex-1 relative z-10 overflow-y-auto flex flex-col pb-32 pt-6"
        onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 0)}
      >
        <div className="flex-1 flex flex-col px-5 md:px-8 max-w-3xl mx-auto w-full">
        {isLoading || !transcript ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-20">
            <CircularProgress
              progress={progress}
              size="lg"
              indeterminate={stillWorking}
            />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 mt-6 tracking-tight">
              {t("flashcard.flashcard")}
            </h3>
            <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed">
              {stillWorking
                ? t("aiContent.stillWorkingSubtitle")
                : t("flashcard.generatingFlashcard", { progress })}
            </p>
          </div>
        ) : flashcards.length > 0 && currentCard ? (
          <div className="flex-1 flex flex-col justify-center max-w-md md:max-w-lg mx-auto w-full pb-20">
            <div
              onClick={() => setIsFlipped(!isFlipped)}
              data-action-id="flashcard.flip"
              className="relative w-full aspect-[4/5] [perspective:1000px] cursor-pointer group"
            >
              <div
                className={`w-full h-full transition-all duration-500 [transform-style:preserve-3d] relative ${isFlipped ? "[transform:rotateY(180deg)]" : ""}`}
              >
                {/* Front */}
                <div className="absolute inset-0 backface-hidden w-full h-full">
                  <div className="glass w-full h-full rounded-[40px] p-8 flex flex-col items-center justify-center text-center active:scale-[0.98] hover:shadow-xl transition-all duration-200">
                    <span className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-widest mb-6">
                      {t("quiz.question")}
                    </span>
                    <h2
                      dir="auto"
                      className="text-2xl md:text-3xl font-bold leading-relaxed text-[var(--text-primary)]"
                    >
                      {currentCard.question}
                    </h2>
                    <div className="absolute bottom-8 text-[var(--text-tertiary)] text-sm font-medium opacity-50">
                      {t("flashcard.tapToFlip")}
                    </div>
                  </div>
                </div>

                {/* Back */}
                <div className="absolute inset-0 backface-hidden w-full h-full [transform:rotateY(180deg)]">
                  <div className="w-full h-full rounded-[40px] bg-[var(--text-primary)] backdrop-blur-md p-8 flex flex-col items-center justify-center text-center shadow-lg active:scale-[0.98] hover:shadow-xl transition-all duration-200">
                    <span className="text-xs font-bold text-[var(--bg-primary)] uppercase tracking-widest mb-6 opacity-70">
                      {t("flashcard.answer")}
                    </span>
                    <h2
                      dir="auto"
                      className="text-2xl md:text-3xl font-bold leading-relaxed text-[var(--bg-primary)]"
                    >
                      {currentCard.correctAnswer}
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
            <div className="glass w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <SparklesIcon size={32} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              {t("flashcard.flashcardFailedDescription")}
            </h3>
            <button
              onClick={() => {
                void handleRegenerate();
              }}
              data-action-id="flashcard.regenerate"
              className="px-6 py-3 rounded-full bg-[var(--text-primary)] text-[var(--bg-primary)] font-semibold active:scale-95 hover:opacity-90 transition-all"
            >
              {t("flashcard.tryAgain")}
            </button>
          </div>
        )}
        </div>
      </main>

      {/* Bottom Navigation */}
      {flashcards.length > 0 && !isLoading && (
        <BottomActionBar compactBottom className="flex justify-center">
          <div className="glass-thick inline-flex items-center gap-6 p-2 rounded-full">
            <button
              onClick={handlePrevious}
              data-action-id="flashcard.previous"
              disabled={currentCardIndex === 0}
              className="icon-btn icon-btn-quiet w-14 h-14 text-[var(--text-primary)] disabled:opacity-30 active:scale-90 transition-all"
            >
              <ChevronLeftIcon size={28} className="rtl:-scale-x-100" />
            </button>

            <div className="px-4 flex flex-col items-center min-w-[60px]">
              <span
                dir="ltr"
                className="text-lg font-bold text-[var(--text-primary)] tabular-nums"
              >
                {currentCardIndex + 1} / {flashcards.length}
              </span>
            </div>

            <button
              onClick={handleNext}
              data-action-id="flashcard.next"
              disabled={currentCardIndex === flashcards.length - 1}
              className="icon-btn icon-btn-quiet w-14 h-14 text-[var(--text-primary)] disabled:opacity-30 active:scale-90 transition-all"
            >
              <ChevronRightIcon size={28} className="rtl:-scale-x-100" />
            </button>
          </div>
        </BottomActionBar>
      )}
    </Container>
  );
}
